'use client';

import React from 'react';
import { Box, Typography, Card, CardContent, Alert } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { CalendarProvider, CalendarProviderConfig } from './types';

interface CalendarProviderSelectorProps {
    onProviderSelect: (provider: CalendarProvider) => void;
    selectedProvider?: CalendarProvider | null;
    title?: string;
    description?: string;
    showAlert?: boolean;
    variant?: 'cards' | 'list';
}

const defaultProviders: CalendarProviderConfig[] = [
    {
        id: 'google',
        name: 'Google Calendar',
        description: 'Connect directly to your Google Calendar',
        icon: GoogleIcon,
        color: '#C0C0DD'
    },
    {
        id: 'tripleseat',
        name: 'Tripleseat',
        description: 'Export from Tripleseat to Google Calendar',
        icon: CalendarMonthIcon,
        color: '#C0C0DD'
    }
];

const CalendarProviderSelector: React.FC<CalendarProviderSelectorProps> = ({
    onProviderSelect,
    selectedProvider,
    title = "Which calendar system do you use?",
    description,
    showAlert = true,
    variant = 'cards'
}) => {
    return (
        <Box>
            {description && (
                <Typography variant="body1" paragraph className="text-gray-300 mb-6">
                    {description}
                </Typography>
            )}

            <Typography variant="h6" gutterBottom className="mt-4 mb-4 text-white font-semibold">
                {title}
            </Typography>

            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: variant === 'cards' ? 'row' : 'column' },
                gap: 3,
                mb: showAlert ? 5 : 3
            }}>
                {defaultProviders.map((provider) => {
                    const IconComponent = provider.icon;
                    const isSelected = selectedProvider === provider.id;

                    return (
                        <Card
                            key={provider.id}
                            sx={{
                                width: variant === 'cards' ? { xs: '100%', sm: '50%' } : '100%',
                                cursor: 'pointer',
                                bgcolor: isSelected ? 'rgba(224, 177, 241, 0.2)' : '#1C1C34',
                                '&:hover': { bgcolor: 'rgba(224, 177, 241, 0.1)' },
                                borderRadius: 3,
                                border: isSelected
                                    ? '2px solid #E0B1F1'
                                    : '1px solid rgba(224, 177, 241, 0.2)',
                                boxShadow: isSelected
                                    ? '0 4px 16px rgba(224, 177, 241, 0.3)'
                                    : '0 4px 12px rgba(0, 0, 0, 0.25)',
                                transition: 'all 0.2s ease-in-out'
                            }}
                            onClick={() => onProviderSelect(provider.id)}
                        >
                            <CardContent sx={{
                                display: 'flex',
                                flexDirection: variant === 'cards' ? 'column' : 'row',
                                alignItems: variant === 'cards' ? 'center' : 'flex-start',
                                p: 4,
                                gap: variant === 'cards' ? 0 : 2
                            }}>
                                <IconComponent sx={{
                                    fontSize: variant === 'cards' ? 48 : 32,
                                    mb: variant === 'cards' ? 2 : 0,
                                    color: provider.color,
                                    flexShrink: 0
                                }} />
                                <Box sx={{
                                    textAlign: variant === 'cards' ? 'center' : 'left',
                                    flex: 1
                                }}>
                                    <Typography variant="h6" gutterBottom className="text-white">
                                        {provider.name}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className={`${variant === 'cards' ? 'mt-1' : ''} text-gray-300`}
                                    >
                                        {provider.description}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>

            {showAlert && (
                <Alert
                    severity="info"
                    sx={{
                        mb: 4,
                        bgcolor: 'rgba(33, 150, 243, 0.05)',
                        borderRadius: 2,
                        border: '1px solid rgba(33, 150, 243, 0.2)',
                        color: 'white',
                        '& .MuiAlert-icon': {
                            color: '#C0C0DD'
                        }
                    }}
                >
                    Don&apos;t see your calendar system? Connect any calendar by adding it to Google Calendar first, then connecting Google Calendar to Litty.
                </Alert>
            )}
        </Box>
    );
};

export default CalendarProviderSelector; 